import { Component, Input, forwardRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { MAT_DATE_RANGE_SELECTION_STRATEGY, MatDateRangeInput } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { WeekRangeSelectionStrategy } from './form-week-picker-strategy.service';
import { DateTime } from 'luxon';
import { STATISTICS_START_DATE } from '@app/constants';

interface WeekRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'flow-form-week-picker',
  templateUrl: './form-week-picker.component.html',
  styleUrls: ['./form-week-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormWeekPickerComponent),
      multi: true,
    },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: WeekRangeSelectionStrategy,
    },
  ],
})
export class FormWeekPickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  destroy$ = new Subject<void>();

  @ViewChild(MatDateRangeInput) dateRangeInput: MatDateRangeInput<Date>;
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() infoTooltip: string = '';
  @Input() fromPlaceholder: string = 'global.startDate';
  @Input() toPlaceholder: string = 'global.endDate';
  @Input() disabled: boolean = false;
  @Input() showClearBtn: boolean = true;
  @Input() disableWeekStartDate: DateTime;

  @Input() maxDate = DateTime.local().endOf('week');
  @Input() minDate = STATISTICS_START_DATE;
  startDate: Date | null = null;

  form: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  onChange: (value: WeekRange) => void = () => {};
  onTouched: () => void = () => {};
  isLoaded = false;

  constructor(private translate: TranslateService) {}

  ngOnInit() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (!this.disabled) {
        this.onChange(value as WeekRange);
        this.onTouched();
      }
    });

    this.translate
      .get([this.fromPlaceholder, this.toPlaceholder])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isLoaded = true;
      });
  }

  writeValue(value: WeekRange): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: WeekRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  clearWeekPicker(): void {
    this.form.reset();
  }

  getWeekDates(date: DateTime): DateTime[] {
    const startOfWeek = date.startOf('week');
    return Array.from({ length: 7 }, (_, i) => startOfWeek.plus({ days: i }));
  }

  dateFilter = (date: DateTime | null): boolean => {
    if (!this.disableWeekStartDate) return true;

    const weekDates = this.getWeekDates(this.disableWeekStartDate);
    const disableDate = weekDates
      ? !weekDates.some(disabledDate => {
          const val = disabledDate.hasSame(date, 'day');
          return val;
        })
      : true;

    return disableDate;
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
